import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV2Entity,
  type CompositionV3DataQuery,
  type CompositionV3DataQueryVariables,
  type GetPayV2Query,
  PAY_V2_QUERY,
  type PayV2Entity,
  type PayV2Req,
} from '@revelio/data-access';

import { environment } from '../../../../../environments/environment';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { getCompositionV3Variables } from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { getV2Filters } from '../../../filters/utils/getV2Filters';
import { mapPositionsEntitiesToCompositionV3 } from '../../../filters/utils/role-skill-ent-kind';
import { isCompositionV3FullMetrics } from '../composition/composition-v3/composition-v3-graphql-response';
import { combineTwoQueryStatuses } from '../market-conditions/market-condition-metrics-utils';
import { DataProviderQueryStatus } from '../types';
import { useUrqlQueryWithStatus } from '../utils/data-provider-results-utils';
import { getSegmentEntityType } from './getSegmentEntityType';

const isProd = environment.production;

const MAX_SEGMENT_ENTITIES = isProd ? 6 : 10;

type PayAndCompositionParams = {
  primaryDimension: PrimaryFilterId;
  secondaryDimension: 'geography' | 'role';
  entitiesOverride?: CompositionV2Entity[];
};

export function usePayAndCompositionQueries({
  primaryDimension,
  secondaryDimension,
  entitiesOverride,
}: PayAndCompositionParams) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const geographyMode = useGeographyMode();

  const compositionVariablesV3 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    geographyMode,
  });

  const compositionEntitiesV3 = (() => {
    if (Array.isArray(entitiesOverride) && entitiesOverride.length > 0) {
      return mapPositionsEntitiesToCompositionV3(entitiesOverride) ?? [];
    }
    if (Array.isArray(compositionVariablesV3.entities)) {
      return compositionVariablesV3.entities;
    }
    return compositionVariablesV3.entities
      ? [compositionVariablesV3.entities]
      : [];
  })();

  const compositionVariablesFinalV3 = {
    ...compositionVariablesV3,
    entities: compositionEntitiesV3,
  };

  const pauseCompositionV3 =
    compositionVariablesFinalV3.entities.length === 0 ||
    !compositionVariablesFinalV3.filters?.start_date ||
    !compositionVariablesFinalV3.filters?.end_date;

  const [{ data: compositionDataUnion, status: compositionStatusUnion }] =
    useUrqlQueryWithStatus<
      CompositionV3DataQuery,
      CompositionV3DataQueryVariables
    >({
      query: COMPOSITION_GET_DATA_V3,
      variables: compositionVariablesFinalV3,
      pause: pauseCompositionV3,
    });

  const compositionRes = compositionDataUnion?.composition_v3;

  const segmentEntityType = compositionRes
    ? getSegmentEntityType({
        secondaryDimension,
        compositionFilters: compositionVariablesFinalV3.filters,
      })
    : null;

  const segmentPayEntities: PayV2Entity[] = (() => {
    if (!compositionRes?.entities?.length || segmentEntityType === null) {
      return [];
    }
    const seen = new Set<number>();
    const out: PayV2Entity[] = [];
    for (const ent of compositionRes.entities) {
      const segments =
        (isCompositionV3FullMetrics(ent.metrics) ? ent.metrics : undefined)?.[
          secondaryDimension
        ] ?? [];
      for (const seg of segments) {
        if (seen.size >= MAX_SEGMENT_ENTITIES) break;
        const id = (seg as { id: number }).id;
        if (id !== undefined && !seen.has(id)) {
          seen.add(id);
          out.push({ EntID: id, EntType: segmentEntityType });
        }
      }
      if (seen.size >= MAX_SEGMENT_ENTITIES) break;
    }
    return out;
  })();

  const payFilterPrimaryDimension =
    secondaryDimension === 'geography' ? FilterId.GEOGRAPHY : FilterId.ROLE;
  const payFilters = getV2Filters({
    primaryDimension: payFilterPrimaryDimension,
    effectiveFilters,
    isSnapshot: false,
  });

  const payVariables: PayV2Req = {
    entities: segmentPayEntities,
    filters: payFilters,
    ...(geographyMode === 'administrative' && {
      options: { geo_breakdown_state_over_msa: true },
    }),
  };

  const compositionOk =
    !pauseCompositionV3 &&
    compositionStatusUnion === DataProviderQueryStatus.Success;

  const pausePay =
    pauseCompositionV3 ||
    !compositionOk ||
    !compositionRes ||
    segmentPayEntities.length === 0 ||
    !payVariables.filters?.start_date ||
    !payVariables.filters?.end_date;

  const [{ data: payData, status: payStatus }] =
    useUrqlQueryWithStatus<GetPayV2Query>({
      query: PAY_V2_QUERY,
      variables: { req: payVariables },
      pause: pausePay,
    });

  const status = combineTwoQueryStatuses(compositionStatusUnion, payStatus);

  return {
    payData: payData?.pay_v2 ?? undefined,
    compositionData: compositionDataUnion?.composition_v3,
    status,
  };
}
